<template>
  <div class="min-h-screen flex bg-bg text-text">
    <!-- desktop sidebar -->
    <div class="hidden md:flex shrink-0">
      <UiSidebar
        :items="navItems"
        :collapsed="collapsed"
        :user="user"
        @toggle="toggleCollapsed"
        @logout="logout"
      />
    </div>
    <!-- mobile drawer -->
    <Transition name="drawer">
      <div v-if="mobileOpen" class="fixed inset-0 z-40 md:hidden">
        <div class="absolute inset-0 bg-black/70" @click="mobileOpen = false"></div>
        <div class="relative flex h-full">
          <UiSidebar
            :items="navItems"
            :collapsed="false"
            :user="user"
            @toggle="mobileOpen = false"
            @logout="logout"
          />
        </div>
      </div>
    </Transition>
    <!-- main -->
    <main class="flex-1 min-w-0 overflow-x-hidden">
      <div class="px-5 md:px-8 py-5 md:py-6 max-w-[1500px] mx-auto">
        <slot :toggle-nav="() => mobileOpen = true" />
      </div>
    </main>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import {
  Users, UserCircle, MessageSquare, Server, Database, Activity, KeyRound,
} from 'lucide-vue-next';
import UiSidebar from './UiSidebar.vue';

const store = useStore();
const router = useRouter();

const collapsed = ref(false);
const mobileOpen = ref(false);

onMounted(() => {
  collapsed.value = localStorage.getItem('nf.sidebar.collapsed') === '1';
});
watch(collapsed, v => localStorage.setItem('nf.sidebar.collapsed', v ? '1' : '0'));

function toggleCollapsed() { collapsed.value = !collapsed.value; }

const user = computed(() => store.state.userProfile);
const isSuperAdmin = computed(() => user.value?.role === 'SUPER_ADMIN');

const navItems = computed(() => {
  const groups = [
    {
      category: 'Accounts',
      links: [
        { label: 'Users',      icon: Users,         to: '/home' },
        { label: 'My Account', icon: UserCircle,    to: '/my-account' },
        { label: 'Messages',   icon: MessageSquare, to: '/messages' },
      ],
    },
  ];
  if (isSuperAdmin.value) {
    groups.push({
      category: 'MCS Operations',
      links: [
        { label: 'Sites',             icon: Server,   to: '/admin/sites' },
        { label: 'Customers Catalog', icon: Database, to: '/admin/mcs-customers' },
        { label: 'Sync Health',       icon: Activity,  to: '/admin/sync-health' },
        { label: 'API Keys',          icon: KeyRound,  to: '/admin/api-keys' },
      ],
    });
  }
  return groups;
});

async function logout() {
  await store.dispatch('logout');
  router.push({ name: 'login' });
}
</script>

<style scoped>
.drawer-enter-active, .drawer-leave-active { transition: opacity var(--dur) var(--ease); }
.drawer-enter-from, .drawer-leave-to { opacity: 0; }
</style>
